import { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { twMerge } from 'tailwind-merge';
import { ProcessIcon } from '@/components/shared/ProcessIcon';

// Redux
import { useDispatch } from 'react-redux';
import {
  setSelectedUnit,
  setIsApartmentView,
} from '@/features/project/projectSlice';

import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

import {
  ActionItem,
  ActionItemCategory,
  BuildingWorkflowResponse,
  FeedEvent,
  Process,
  Unit,
  UserData,
  UserRef,
} from '@urbanmix-tech/shared-js';
import { comparePseudeDates, convertPseudoDateToRealDate } from '@/helpers/common';

import { ITableCell, ITableRow } from '@/components/shared/Table/Table';
import { VirtualizedTable } from '@/components/shared/Table';

import { useAuth } from '@/shared/context/AuthProvider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import type { RootState } from '@/store';
import { useSelector } from 'react-redux';
import { ApartmentData } from '@/processes/AnalyzeUnits';
import {
  deleteProcess,
  deleteActionItem,
  getFeedEvents,
  saveFeedEvent,
  saveProcess,
  subscribeToFeedEvents,
} from '@/data';
import { borderColorClassByCategory } from '../UnitTabs/Todo/TodoAccordion';

import { UsersAvatars } from '../shared/UserAvatar/UserAvatars';

import { ActionCategoryCircle } from '../UnitTabs/Todo/ActionCategoryCircle';
import { useBuildingModel } from '../../contexts/Building/BuildingContext';
import { TaskList } from './TasksList';
import WorkflowsProvider from '../BuildingWorkflows/WorkflowsProvider';
import { FeedEventDetailsModal } from './FeedEventDetailsModal';
import { ProcessEditModal } from './ProcessEditModal';
import { useProcessSettings } from '@/shared/context/ProcessSettingsProvider';
import { Box } from '@mui/material';

const oneDayGap = 1 * 24 * 60 * 60 * 1000;

export type TrackerProcessGroupProps = {
  process: Process;
  category: ActionItemCategory;
  actionItems: ActionItem[];
  expanded?: boolean;
  // onChangeExpanded: (category: ActionItemCategory, expanded: boolean) => void;
  onClickActionItem(actionItem: ActionItem): void;
  onHoverItem?(actionItem?: ActionItem): void;
  unitId: string;
  onProcessClick?(process: Process): void;
  editMode?: boolean;
  selected?: boolean;
};

export function TrackerProcessGroup({
  process,
  category,
  actionItems,
  expanded,
  // onChangeExpanded,
  onClickActionItem,
  onHoverItem,
  unitId,
  onProcessClick,
  editMode,
  selected,
}: TrackerProcessGroupProps) {
  // Mount Redux dispatcher
  const dispatch = useDispatch();
  const { getProcessTypeConfig } = useProcessSettings();

  const apartmentData = useSelector(
    (state: RootState) => state.project.apartmentData
  ) as ApartmentData[];

  const projectVisualizer = useSelector(
    (state: RootState) => state.project.projectVisualizer
  );

  const hoveredApartmentId = useSelector(
    (state: RootState) => state.project.hoveredUnitId
  );

  const units = useSelector(
    (state: RootState) => state.project.projectUnits
  ) as Unit[];


  const authContext = useAuth();
  const { usersData } = authContext;
  const currentUserId = authContext.auth.currentUser?.uid;
  const currentUser: UserData | null = currentUserId
    ? usersData[currentUserId]
    : null;

  const { selectUnit, setUnitOverlayData } = useBuildingModel();

  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [showAddEventLog, setShowAddEventLog] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [feedEvents, setFeedEvents] = useState<FeedEvent[]>([]);
  const [runningEvent, setRunningEvent] = useState<{
    event: FeedEvent;
    error: string | null;
  } | null>(null);

  const [selectedEvent, setSelectedEvent] = useState<FeedEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditProcessModalOpen, setIsEditProcessModalOpen] =
    useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

  const unit = useMemo(() => {
    return units.find((u) => u.id === unitId);
  }
  ,[units, unitId]);

  const userGroupTasksTypes = useMemo(() => {
    const result = {
      hasOwnerActiveTasks: false,
      hasSupervisorActiveTasks: false,
      hasSupervisorLateTasks: false,
      hasFollowersActiveTasks: false,
      hasAnyActiveItems: false,
      hasLateTasks: false,
    };
    actionItems.forEach((item) => {
      if (item.owner?.userId === currentUserId && item.status !== 'done') {
        result.hasOwnerActiveTasks = true;
      }
      if (item.supervisor?.userId === currentUserId && item.status !== 'done') {
        result.hasSupervisorActiveTasks = true;
      }
      const completedOn = convertPseudoDateToRealDate(item.completedOn);
      const dueDate = convertPseudoDateToRealDate(item.dueDate) || new Date();
      const now = new Date().getTime();

      if (completedOn && completedOn.getTime() > now - 3600) {
        result.hasOwnerActiveTasks = true;
        result.hasAnyActiveItems = true;
      }
      if (
        item.supervisor?.userId === currentUserId &&
        item.status !== 'done' &&
        dueDate < new Date()
      ) {
        result.hasSupervisorLateTasks = true;
      }
      if (
        item.followers &&
        item.followers.find((f) => f.userId === currentUserId) &&
        item.status !== 'done'
      ) {
        result.hasFollowersActiveTasks = true;
      }
      if (item.status !== 'done') {
        result.hasAnyActiveItems = true;
      }
      if (dueDate < new Date() && item.status === 'active') {
        result.hasLateTasks = true;
      }
    });
    return result;
  }, [actionItems, currentUserId]);

  useEffect(() => {
    const subscribeToEvents = async () => {
      const unsubscribe = await subscribeToFeedEvents(
        { processId: process.id },
        (events) => {
          // console.log('got new feed events in subscription', events);
          const filtered = events
            .filter((event) => {
              return event.processId === process.id;
            })
            .sort((a, b) => comparePseudeDates(a.eventDate, b.eventDate));        
          setFeedEvents(filtered);
        },
        (error) => {
          console.log('error subscribing to feed events', error);
        }
      );

      return () => unsubscribe();
    };
    const unsubscribePromise = subscribeToEvents();
    return () => {
      unsubscribePromise.then((unsubscribe) => unsubscribe());
    };
  }, []);

  const activeGroupUsers = useMemo(() => {
    const users = new Set<string>();
    actionItems.forEach((item) => {
      if (item.owner?.userId && item.status !== 'done') {
        users.add(item.owner.userId);
      }
    });
    return Array.from(users).map((userId) => {
      const userData = usersData[userId];
      return {
        userId,
        email: userData?.email ?? '',
        firstName: userData?.fullName?.split(' ')[0] ?? userData?.email ?? '',
      };
    }) as (UserRef & { firstName: string })[];
  }, [actionItems, usersData]);

  const [showExpanded, setShowExpanded] = useState<boolean>(expanded ?? false);
  const [hasActiveItems, setHasActiveItems] = useState<boolean>(false);
  const [showDetailsPanel, setShowDetailsPanel] = useState<boolean>(false);

  const [hovering, setHovering] = useState<boolean>(false);

  const onAddLogEvent = (e: React.MouseEvent) => {
    // console.log('adding event',e.currentTarget.innerText);
    const eventType = (e.currentTarget as HTMLButtonElement).innerText;
    const newEvent: FeedEvent = {
      id: `${eventType}_${new Date().getTime()}`,
      eventType,
      eventDate: new Date(),
      projectId: process.projectId!,
      processId: process.id,
      buildingId: 1,
      unitId: process.unitId!,
      eventData: {
        manual: true,
        type: "completion",
      },
    };
    saveFeedEvent(newEvent).then((result) => {
      console.log('event saved', result);
      if (result) {
        setFeedEvents([...feedEvents, newEvent]);
      }
    });
  };

  const onClearRunningEvent = () => {
    if (!runningEvent) return;

    // Clear the current running event
    const updatedEvent: FeedEvent = {
      ...runningEvent.event,
      eventData: {
        ...runningEvent.event.eventData,
        visible: false,
      },
    };

    console.log('Clearing event with updated data:', updatedEvent);

    saveFeedEvent(updatedEvent).then((result) => {
      console.log('event cleared', result);
    });
  };

  const onClearAllSpinnerEvents = () => {
    // Clear all spinner events that don't have visible: false
    const spinnerEvents = feedEvents.filter(
      event => event.eventData.type === 'spinner' && event.eventData.visible !== false
    );

    console.log(`Clearing ${spinnerEvents.length} spinner events`);

    spinnerEvents.forEach(event => {
      const updatedEvent: FeedEvent = {
        ...event,
        eventData: {
          ...event.eventData,
          visible: false,
        },
      };
      saveFeedEvent(updatedEvent);
    });
  };

  const handleDeleteProcess = async (deleteTasksAlso: boolean) => {
    console.log('delete process', process, 'delete tasks:', deleteTasksAlso);

    if (deleteTasksAlso) {
      // Delete all tasks associated with this process
      const deletePromises = actionItems.map(item => deleteActionItem(item.id));
      await Promise.all(deletePromises);
      console.log(`Deleted ${actionItems.length} tasks`);
    }

    // Delete the process
    await deleteProcess(process.id);
    setIsDeleteDialogOpen(false);
  };

  useEffect(() => {
    // lets see if there's a spinner event running
    let running: FeedEvent | null = null as FeedEvent | null;
    let error: string | null = null;

    if (process.status === 'cancelled') {
      setRunningEvent(null);
      return;
    }

    // Process events in reverse order (newest first) to find the most recent running event
    for (let i = 0; i < feedEvents.length; i++) {
      const event = feedEvents[i];
      // console.log('checking event', event.id, 'visible:', event.eventData.visible, 'type:', event.eventData.type);

      const isVisible = event.eventData.visible !== false;
      // console.log('isVisible check:', isVisible, 'visible value:', event.eventData.visible);

      // Only process if visible

      // Check if this event completes a previously running spinner
      const currentRunning = running;
      if (currentRunning && event.eventType === currentRunning.eventData.successEvent) {
        console.log('Found success event for running spinner, clearing running');
        running = null;
        error = null;
        continue;
      }

      // Check for spinner events
      if (event.eventData.type === 'spinner') {
        console.log('Found visible spinner event:', event.id);
        // check if timeout passed
        const now = new Date().getTime();
        const startedAt =
          convertPseudoDateToRealDate(event.eventDate)?.getTime() ?? -Infinity;
        const diff = now - startedAt;
        running = event;
        error = null;
        // console.log('diff', diff);
        if (
          !event.eventData?.timeout ||
          diff > event.eventData.timeout * 1000
        ) {
          // console.log('timeout passed', diff, event.eventData.timeout * 1000);
          error = 'Timeout';
        }
        continue; // Stop at the first (most recent) spinner we find
      }

      // Check for completion events with errors
      if (event.eventData.type === 'completion' && event.eventData.error) {
        running = event;
        error = event.eventData.error;
        continue; // Stop at the first (most recent) error we find
      }
    }

    if (running) {
      console.log('Final running event:', running.id, 'error:', error);
      // TODO: set a timer to check the event at the end of the timeout
      setRunningEvent({ event: running, error });
    } else {
      console.log('No running event found');
      setRunningEvent(null);
    }
  }, [feedEvents, process.status]);

  useEffect(() => {
    const expandedGroup =
      userGroupTasksTypes.hasOwnerActiveTasks ||
      userGroupTasksTypes.hasSupervisorLateTasks;
    setShowExpanded(true);
    setHasActiveItems(userGroupTasksTypes.hasAnyActiveItems);
    selectUnit(expandedGroup ? unitId : null);
  }, [
    selectUnit,
    unitId,
    userGroupTasksTypes.hasAnyActiveItems,
    userGroupTasksTypes.hasOwnerActiveTasks,
    userGroupTasksTypes.hasSupervisorLateTasks,
  ]);

  const { groupColor, groupIcon } = useMemo(() => {
    let color: any = {};
    let icon: string = '';
    if (userGroupTasksTypes.hasOwnerActiveTasks && currentUser) {
      icon = 'owner';
    }
    if (userGroupTasksTypes.hasLateTasks) {
      icon = 'late';
    }
    if (!userGroupTasksTypes.hasAnyActiveItems) {
      icon = 'done';
    }

    if (icon === '') {
      icon = 'waiting';
    }

    // ts-ignore
    const data = apartmentData?.find((item) => item.apartmentId === unitId);

    if (data) {
      color = projectVisualizer.getColor(data.value);
    } else {
      color = null;
    }

    const colorClass = `!bg-[${color}]`;
    return {
      groupColor: { color, class: colorClass },
      groupIcon: icon,
    };
  }, [
    activeGroupUsers,
    currentUser,
    userGroupTasksTypes.hasAnyActiveItems,
    userGroupTasksTypes.hasLateTasks,
    userGroupTasksTypes.hasOwnerActiveTasks,
    projectVisualizer,
    apartmentData,
    unitId,
    activeGroupUsers,
  ]);

  useEffect(() => {
    if (hoveredApartmentId == unitId) {
      setHovering(true);
    } else {
      setHovering(false);
    }
  }, [hoveredApartmentId]);

  const processConfig = getProcessTypeConfig(process.type || '');

  return (
    <Accordion
      expanded={showExpanded}
      onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onChange={() => {
      }}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&:before': {
            display: 'none',
        },
      }}
      className={twMerge("!m-0 rounded-lg bg-zinc-900 border border-white/5 mb-16 overflow-hidden")}
    >
      <AccordionSummary
        className="!m-0 !min-h-0 overflow-hidden !px-0 pt-[12px]"
        sx={{
          '& .MuiAccordionSummary-content': { margin: '0 !important' },
          cursor: 'default'
        }}
      >
        <Stack
          direction="row"
          gap="16px"
          alignItems="center"
            className="flex pb-[16px]"
            sx={{ width: '100%' }}
          >
          <Button
            onClick={(e: React.MouseEvent) => {
              dispatch(setSelectedUnit({ apartmentId: unitId }));
              e.stopPropagation();
            }}
            className="!min-w-0 !p-0 !ml-[16px] !rounded-lg overflow-hidden border border-white/10"
            sx={{
               width: 'auto',
               height: 36,
            }}
          >
            <div 
                className="flex items-center justify-center w-full h-full px-[12px] bg-zinc-800/70 gap-[8px]"
            >
                <div 
                    className="w-[8px] h-[8px] rounded-full"
                    style={{ backgroundColor: processConfig.color }}
                />
                <Typography variant="body2" className="font-bold text-white whitespace-nowrap">
                    {process.unitId}
                </Typography>
            </div>
          </Button>

          <Stack
            direction="row"
            gap="8px"
            alignItems="center"
            className="flex-1"
          >
            {editMode && (
              <Button
                onClick={() => onProcessClick?.(process)}
                variant={selected ? 'outlined' : 'text'}
              >
                Select
              </Button>
            )}
            {process.status === 'start' && (
              <AutorenewOutlinedIcon className="text-zinc-400" sx={{ fontSize: 18 }} />
            )}
            {process.status === 'completed' && (
              <CheckCircleOutlinedIcon className="text-zinc-400" sx={{ fontSize: 18 }} />
            )}
            {process.status === 'cancelled' && (
              <CancelOutlinedIcon className="text-zinc-400" sx={{ fontSize: 18 }} />
            )}
            <div className="flex flex-col flex-1 pl-4">
               <Typography
                variant="h6"
                className={twMerge(
                  'font-semibold leading-tight tracking-tight',
                  process.status === 'cancelled'
                    ? 'text-zinc-500 line-through'
                    : 'text-stone-200'
                )}
              >
                {processConfig.displayName}
              </Typography>
              {/* {activeGroupUsers.length > 0 && <Typography variant="caption" className="text-zinc-500 mt-0.5">
                   Assigned to {activeGroupUsers.map(u => u.firstName).join(', ')}
              </Typography>} */}
            </div>

            <Stack direction="row" gap={1} alignItems="center" className="flex-none">
             {unit && unit.affordable && (
            <Tooltip title="Affordable">
              <ShieldOutlinedIcon className="text-zinc-400"/>
              </Tooltip>)}
            <Tooltip title={showDetailsPanel ? "Hide details" : "Show details"}>
              <IconButton
                size="small"
                onClick={() => setShowDetailsPanel(!showDetailsPanel)}
              >
                <MoreHorizIcon color="action" />
              </IconButton>
            </Tooltip>

            {!showExpanded && <UsersAvatars users={activeGroupUsers} />}
          </Stack>
        </Stack>
      </Stack>
      </AccordionSummary>
      <hr className="border-gray-700" />
      {
        <>
          <div
            className={twMerge(
              'transition-discrete start:hidden flex-1 px-16 py-12 transition-all duration-500',
              showDetailsPanel ? 'block' : 'hidden'
            )}
          >
            <Typography variant="body2" className="pb-12 text-zinc-400 text-sm font-normal">
              Process Log {process.id}
            </Typography>
            <div className="flex flex-col gap-12">
              {feedEvents.map((event) => {
                const date = convertPseudoDateToRealDate(event.eventDate);
                return (
                  <div key={event.id} className="flex flex-row gap-12">
                    <Button
                      onClick={() => {
                        setSelectedEvent(event);
                        setIsModalOpen(true);
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        className={twMerge(
                          "text-zinc-400 text-sm font-normal",
                          process.status === 'cancelled' ? 'line-through' : ''
                        )}
                      >
                        {date?.toLocaleDateString()}{' '}
                        {date?.toLocaleTimeString()}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        className={twMerge(
                          "text-zinc-400 text-sm font-normal",
                          process.status === 'cancelled' ? 'line-through' : ''
                        )}
                      >
                        {event.eventData.title ?? event.eventType}
                      </Typography>
                    </Button>
                  </div>
                );
              })}
            </div>
            <FeedEventDetailsModal
              open={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setSelectedEvent(null);
              }}
              event={selectedEvent}
            />
            <Stack direction="row" gap="8px">
            <Button
              id="add-log-button"
              variant="outlined"
              onClick={(e: React.MouseEvent) => {
                setAnchorEl(e.currentTarget as HTMLElement);
                  setShowAddEventLog(true);
              }}
              sx={{
                color: '#e7e5e4 !important',
                borderColor: 'rgba(255, 255, 255, 0.1) !important',
                '&:hover': {
                  color: '#3b82f6 !important',
                  borderColor: '#3b82f6 !important',
                  backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                },
              }}
            >
              Add Event 
            </Button>
            <Button
              id="edit-process-button"
              variant="outlined"
              onClick={() => setIsEditProcessModalOpen(true)}
              sx={{
                color: '#e7e5e4 !important',
                borderColor: 'rgba(255, 255, 255, 0.1) !important',
                '&:hover': {
                  color: '#3b82f6 !important',
                  borderColor: '#3b82f6 !important',
                  backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                },
              }}
            >
              Edit Process
            </Button>
            <Button
              id="delete-process-button"
              variant="outlined"
              onClick={() => setIsDeleteDialogOpen(true)}
              sx={{
                color: '#e7e5e4 !important',
                borderColor: 'rgba(255, 255, 255, 0.1) !important',
                '&:hover': {
                  color: '#3b82f6 !important',
                  borderColor: '#3b82f6 !important',
                  backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                },
              }}
            >
              Delete Process
            </Button>
            <Button
              id="complete-process-button"
              variant="outlined"
              onClick={() => {
                console.log('delete process', process);
                saveProcess( { ...process,status: 'completed' });                
              }}
              sx={{
                color: '#e7e5e4 !important',
                borderColor: 'rgba(255, 255, 255, 0.1) !important',
                '&:hover': {
                  color: '#3b82f6 !important',
                  borderColor: '#3b82f6 !important',
                  backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                },
              }}
            >
              Complete Process
            </Button>
            </Stack>
              

            <Menu
              MenuListProps={{ 'aria-labelledby': 'add-log-button' }}
              open={showAddEventLog}
              anchorEl={anchorEl}
              onClose={() => {
                setShowAddEventLog(false);
                // console.log('closing wtf')

                // setAnchorEl(null)
              }}
            >
              <MenuItem key="lease_renewed" onClick={onAddLogEvent}>
                Lease Fully Executed
              </MenuItem>              
              <MenuItem key="tenant_movingout" onClick={onAddLogEvent}>
                Tenant Moving Out
              </MenuItem>
            </Menu>
          </div>
          <hr className="border-gray-700" />
        </>
      }
      <AccordionDetails
        className="p-0"
      >
        <TaskList
          editMode={editMode}
          actionItems={actionItems}
          onClickActionItem={onClickActionItem}
          onHoverItem={(item) => {
            if (item) {
              setHovering(true);
            } else {
              setHovering(false);
            }
            onHoverItem && onHoverItem(item);
          }}
        />
      </AccordionDetails>
      <hr className="border-gray-700" />
      <Stack
        alignItems="left"
        justifyContent="left"
        direction="column"
        gap="8px"
        className="p-16"
      >
        <div>
          {runningEvent ? (
            <div className="flex-1">
              <div className="flex flex-row items-center gap-12">
                {!runningEvent.error && <CircularProgress size={24} />}
                <Typography variant="body2" className="flex-1 text-zinc-400">
                  {runningEvent.event.eventData.title}
                </Typography>
                {runningEvent.error && (
                  <Typography variant="body2" className="text-red-500">
                    Error Occured: {runningEvent.error}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onClearAllSpinnerEvents}
                  className="!text-zinc-400"
                >
                  Clear
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        <div>
          <WorkflowsProvider
            disabled={runningEvent != null && !runningEvent.error}
            process={process}
            projectId={process.projectId ?? undefined}
            unitId={process.unitId ?? undefined}
            onWorkflowStart={(workflow) => {
              // loader.startLoading({ title: `Running ${workflow.name}` });
            }}
            onWorkflowEnd={(workflow, response: BuildingWorkflowResponse) => {
              if (workflow.redirect) {
                // open in a new tab
                console.log('redirecting', response);
              }
              // loader.stopLoading();
              if (response.objects) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const msgs = response.objects.map((obj) => {
                  if (obj.type === 'task' && obj.id) {
                    // handleOpenActionItemById(obj as With
                  }
                });
              }
              if (response.message) {
                // showAlert(response.message, 'success');
              }
            }}
          />
        </div>
      </Stack>      
      <ProcessEditModal
        open={isEditProcessModalOpen}
        onClose={() => setIsEditProcessModalOpen(false)}
        process={process}
        onSave={(updatedProcess) => {
          saveProcess(updatedProcess);
        }}
      />
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Process
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Do you want to delete all {actionItems.length} task{actionItems.length !== 1 ? 's' : ''} associated with this process?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteProcess(false)}
            color="primary"
            variant="outlined"
          >
            Delete Process Only
          </Button>
          <Button
            onClick={() => handleDeleteProcess(true)}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete Process & Tasks
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}
